'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { isAuthMethod } from 'apps/cdp-web-demo/app/data/authMethods';
import {
  defaultAppearanceAppConfig,
  defaultThemeConfig,
  formConfigStorageKey,
  initialState,
} from 'apps/cdp-web-demo/app/data/config';
import {
  AccentColor,
  AccountCreationType,
  AuthMethod,
  BorderRadius,
  ConfigState,
  FontFamily,
  HexColor,
  isHexColor,
  isNonEmptyArray,
  ThemeMode,
} from 'apps/cdp-web-demo/app/types';

import { setCookie } from './utils/cookies';
import { parseQueryParams } from './utils/queryParams';

function mergeConfig(config: ConfigState['config'], newConfig: Partial<ConfigState['config']>) {
  const { ethereum, solana, ...restConfig } = config;
  // only include previous eth/solana config if both are not present in the new config
  const oldConfig = !newConfig.ethereum && !newConfig.solana ? config : restConfig;
  return {
    ...oldConfig,
    ...newConfig,
  };
}

export type Action =
  | { type: 'HYDRATE_STATE'; payload: Partial<ConfigState> }
  | { type: 'RESET_APPEARANCE_CONFIG' }
  | { type: 'SET_ACCOUNT_CREATION_TYPE'; payload: { accountCreationType: AccountCreationType } }
  | { type: 'SET_APP_LOGO'; payload: { appLogoUrl: string } }
  | { type: 'SET_APP_NAME'; payload: { appName: string } }
  | { type: 'SET_AUTH_METHODS_ORDER'; payload: { authMethods: AuthMethod[] } }
  | { type: 'SET_BORDER_RADIUS'; payload: { borderRadius: BorderRadius } }
  | { type: 'SET_ACCENT_COLOR'; payload: { accentColor: AccentColor; customColor?: HexColor } }
  | { type: 'SET_FONT_FAMILY'; payload: { fontFamily: FontFamily } }
  | { type: 'SET_THEME_MODE'; payload: { themeMode: ThemeMode } }
  | { type: 'TOGGLE_AUTH_METHOD'; payload: { method: string; checked: boolean } }
  | { type: 'TOGGLE_SHOW_COINBASE_FOOTER' };

export function configReducer(state: ConfigState, action: Action): ConfigState {
  switch (action.type) {
    case 'HYDRATE_STATE': {
      const newState = action.payload;
      return {
        ...state,
        ...newState,
        customColor:
          !!newState.accentColor && isHexColor(newState.accentColor) && !newState.customColor
            ? newState.accentColor
            : (newState.customColor ?? state.customColor),
        config: mergeConfig(state.config, newState.config ?? {}),
      };
    }
    case 'SET_THEME_MODE': {
      return {
        ...state,
        themeMode: action.payload.themeMode,
      };
    }
    case 'SET_ACCENT_COLOR': {
      return {
        ...state,
        accentColor: action.payload.accentColor,
        customColor: action.payload.customColor ?? '',
      };
    }
    case 'SET_BORDER_RADIUS': {
      return {
        ...state,
        borderRadius: action.payload.borderRadius,
      };
    }
    case 'SET_AUTH_METHODS_ORDER': {
      const validValues = action.payload.authMethods.filter((v) => isAuthMethod(v));
      return {
        ...state,
        config: {
          ...state.config,
          authMethods: isNonEmptyArray(validValues) ? validValues : undefined,
        },
      };
    }
    case 'TOGGLE_AUTH_METHOD': {
      const { method, checked } = action.payload;
      if (!isAuthMethod(method)) {
        return state;
      }
      const currentAuthMethods = state.config.authMethods;
      let newAuthMethods = currentAuthMethods;
      if (checked) {
        newAuthMethods = [...(currentAuthMethods ?? []), method];
      } else if ((currentAuthMethods?.length ?? 0) > 1) {
        const filtered = currentAuthMethods?.filter((v) => v !== method) ?? [];
        newAuthMethods = isNonEmptyArray(filtered) ? filtered : undefined;
      }
      return {
        ...state,
        config: {
          ...state.config,
          authMethods: newAuthMethods,
        },
      };
    }
    case 'SET_APP_LOGO':
      return {
        ...state,
        config: { ...state.config, appLogoUrl: action.payload.appLogoUrl },
      };
    case 'SET_APP_NAME':
      return {
        ...state,
        config: { ...state.config, appName: action.payload.appName },
      };
    case 'SET_FONT_FAMILY': {
      return {
        ...state,
        fontFamily: action.payload.fontFamily,
      };
    }
    case 'TOGGLE_SHOW_COINBASE_FOOTER':
      return {
        ...state,
        config: { ...state.config, showCoinbaseFooter: !state.config.showCoinbaseFooter },
      };
    case 'SET_ACCOUNT_CREATION_TYPE': {
      const value = action.payload.accountCreationType;
      const eth =
        value === 'evm-smart' || value === 'evm-eoa'
          ? ({
              createOnLogin: value.split('-')[1] as 'smart' | 'eoa',
            } as const)
          : undefined;
      const sol =
        value === 'solana'
          ? ({
              createOnLogin: value === 'solana',
            } as const)
          : undefined;
      return {
        ...state,
        config: { ...state.config, ethereum: eth, solana: sol },
      };
    }
    case 'RESET_APPEARANCE_CONFIG': {
      return {
        ...state,
        ...defaultThemeConfig,
        config: {
          ...state.config,
          ...defaultAppearanceAppConfig,
        },
      };
    }
    default:
      return state;
  }
}

function persistentConfigReducer(state: ConfigState, action: Action): ConfigState {
  const newState = configReducer(state, action);
  if (typeof window !== 'undefined') {
    try {
      const { themeMode, accentColor, ...rest } = newState;
      const themeState = { themeMode, accentColor };
      setCookie('cdp-web-demo-theme', JSON.stringify(themeState));
      localStorage.setItem(formConfigStorageKey, JSON.stringify(rest));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error writing to storage', error);
      }
    }
  }
  return newState;
}

type ConfigContextValue = {
  state: ConfigState;
  dispatch: Dispatch<Action>;
  isHydrated: boolean;
};

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

type ConfigProviderProps = {
  children: ReactNode;
  initialConfig?: Partial<Omit<ConfigState, 'config'>> & {
    config?: Partial<ConfigState['config']>;
  };
};

export function ConfigProvider({ children, initialConfig }: ConfigProviderProps) {
  const [state, dispatch] = useReducer(persistentConfigReducer, {
    ...initialState,
    ...initialConfig,
    config: mergeConfig(initialState.config, initialConfig?.config ?? {}),
  });
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    try {
      // First, check for query params
      const queryParamsConfig = parseQueryParams(window.location.search);

      if (queryParamsConfig) {
        // If query params exist, hydrate from them (this will also save to localStorage)
        dispatch({ type: 'HYDRATE_STATE', payload: queryParamsConfig });

        // Remove query params from URL
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.search = '';
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        // Otherwise, load from localStorage
        const storedConfig = localStorage.getItem(formConfigStorageKey);
        if (storedConfig) {
          const parsed = JSON.parse(storedConfig) as Partial<ConfigState>;
          dispatch({ type: 'HYDRATE_STATE', payload: parsed });
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error reading from local storage or query params', error);
      }
    } finally {
      setIsHydrated(true);
    }
  }, []);

  const value = useMemo(() => ({ state, dispatch, isHydrated }), [state, dispatch, isHydrated]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}
